import React, { useEffect, useRef } from "react";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { 
  FaReact, 
  FaNodeJs, 
  FaLaravel,
  FaNpm,
  FaGitAlt
} from "react-icons/fa";
import { 
  SiTailwindcss, 
  SiTypescript, 
  SiSocketdotio,
  SiJavascript,
  SiPython,
  SiPhp,
  SiMongodb,
  SiMysql
} from "react-icons/si";
import { useNavigate } from "react-router-dom";


const About = () => {
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false
  });

  // Animation controls
  const controls = useAnimation();
  const techControls = useAnimation();
  const clientControls = useAnimation();

  // Refs for auto-scrolling
  const techScrollRef = useRef(null);
  const clientScrollRef = useRef(null);

  // Enhanced auto-scroll effect for technologies
  useEffect(() => {
    const scrollContainer = techScrollRef.current;
    if (!scrollContainer) return;

    let scrollAmount = 0;
    let requestId;
    let direction = 1;

    const scroll = () => {
      if (scrollContainer) {
        scrollAmount += 0.5 * direction;
        scrollContainer.scrollLeft = scrollAmount;
        
        if (scrollAmount >= scrollContainer.scrollWidth / 2) {
          direction = -1;
        } else if (scrollAmount <= 0) {
          direction = 1;
        }
      }
      requestId = requestAnimationFrame(scroll);
    };

    requestId = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(requestId);
  }, []);

  // Enhanced auto-scroll effect for clients
  useEffect(() => {
    const scrollContainer = clientScrollRef.current;
    if (!scrollContainer) return;

    let scrollAmount = 0;
    let requestId;
    let direction = 1;

    const scroll = () => {
      if (scrollContainer) {
        scrollAmount += 0.3 * direction;
        scrollContainer.scrollLeft = scrollAmount;
        
        if (scrollAmount >= scrollContainer.scrollWidth / 2) {
          direction = -1;
        } else if (scrollAmount <= 0) {
          direction = 1;
        }
      }
      requestId = requestAnimationFrame(scroll);
    };

    requestId = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(requestId);
  }, []);

  // Trigger animations when in view
  useEffect(() => {
    if (inView) {
      controls.start("visible");
      techControls.start("visible");
      clientControls.start("visible");
    } else {
      controls.start("hidden");
      techControls.start("hidden");
      clientControls.start("hidden");
    }
  }, [inView, controls, techControls, clientControls]);

  // Technologies data with icons
  const technologies = [
    {
      icon: <FaReact size={40} />,
      name: "React",
      color: "#61DAFB",
      description: "Building interactive UIs with modern React hooks"
    },
    {
      icon: <FaNodeJs size={40} />,
      name: "Node.js",
      color: "#68A063",
      description: "Scalable backend services with Node.js runtime"
    },
    {
      icon: <SiTailwindcss size={40} />,
      name: "Tailwind CSS",
      color: "#38B2AC",
      description: "Utility-first CSS for rapid UI development"
    },
    {
      icon: <SiTypescript size={40} />,
      name: "TypeScript",
      color: "#3178C6",
      description: "Type-safe JavaScript for robust applications"
    },
    {
      icon: <SiSocketdotio size={40} />,
      name: "Socket.IO",
      color: "#010101",
      description: "Real-time bidirectional event-based communication"
    },
    {
      icon: <FaLaravel size={40} />,
      name: "Laravel",
      color: "#FF2D20",
      description: "Elegant PHP framework for web artisans"
    },
    {
      icon: <FaNpm size={40} />,
      name: "npm",
      color: "#CB3837",
      description: "Package manager for JavaScript ecosystem"
    },
    {
      icon: <SiJavascript size={40} />,
      name: "JavaScript",
      color: "#F7DF1E",
      description: "The language of the web"
    },
    {
      icon: <SiPython size={40} />,
      name: "Python",
      color: "#3776AB",
      description: "Versatile programming for all purposes"
    },
    {
      icon: <SiPhp size={40} />,
      name: "PHP",
      color: "#777BB4",
      description: "Server-side scripting language"
    },
    {
      icon: <SiMongodb size={40} />,
      name: "MongoDB",
      color: "#47A248",
      description: "NoSQL database for modern applications"
    },
    {
      icon: <SiMysql size={40} />,
      name: "MySQL",
      color: "#4479A1",
      description: "Relational database management system"
    }
  ];

  // Clients data
  const clients = [
    {
      name: "Nuwat Association",
      description: "Non-profit organization",
      logo: "N"
    },
    {
      name: "Beirut Arab University",
      description: "Higher education institution",
      logo: "B"
    },
    {
      name: "Tripoli Municipality",
      description: "Local government",
      logo: "T"
    },
    {
      name: "Lebanese Red Cross",
      description: "Humanitarian organization",
      logo: "L"
    },
    {
      name: "Local Businesses",
      description: "Various small businesses",
      logo: "LB"
    }
  ];

  // Double the arrays for seamless looping
  const doubledTechnologies = [...technologies, ...technologies];
  const doubledClients = [...clients, ...clients];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.6, -0.05, 0.01, 0.99]
      }
    }
  };

  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1, 
      transition: { 
        duration: 1,
        ease: "easeOut"
      } 
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: (i) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.6,
        ease: "backOut"
      }
    })
  };

  const titleLineVariants = {
    hidden: { scaleX: 0, originX: 0 },
    visible: {
      scaleX: 1,
      transition: {
        delay: 0.4,
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <motion.main
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={containerVariants}
      className="about-container"
    >
      {/* Hero Section */}
      <section className="hero-section">
        {/* Animated background elements */}
        <motion.div 
          className="hero-bg-pattern"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />

        <div className="hero-content">
          <motion.div variants={itemVariants}>
            <motion.h1
              className="hero-title"
              variants={itemVariants}
              initial={{ opacity: 0, y: 30 }}
              animate={{ 
                opacity: 1, 
                y: 0,
                transition: { 
                  duration: 0.8,
                  ease: [0.6, -0.05, 0.01, 0.99]
                }
              }}
            >
              About <span className="text-accent">DashWeb</span> Studio
            </motion.h1>
            <motion.p
              className="hero-subtitle"
              variants={itemVariants}
            >
              Building digital solutions with precision and innovation from Tripoli, Lebanon
            </motion.p>
            <motion.div variants={itemVariants}>
              
            </motion.div>
          </motion.div>
        </div>

        {/* Scrolling indicators */}
        <motion.div 
          className="scroll-indicator"
          animate={{
            y: [0, 10, 0],
            opacity: [0.6, 1, 0.6]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <span>Scroll</span>
          <div className="scroll-line" />
        </motion.div>
      </section>

      {/* Story Section */}
      <section className="story-section">
        {/* Animated grid background */}
        <motion.div 
          className="grid-bg"
          animate={{
            backgroundPosition: ['0 0', '40px 40px']
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        
        <div className="story-content">
          <motion.div
            variants={containerVariants}
            className="story-header"
          >
            <motion.div variants={itemVariants}>
              <h2 className="section-title">
                Our <span className="text-accent">Story</span>
              </h2>
              <motion.div 
                className="title-line"
                variants={titleLineVariants}
              />
              <p className="section-subtitle">
                Building digital experiences that matter
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              className="story-text"
            >
              <motion.p variants={itemVariants}>
                Founded in 2020 by <span className="text-accent">Abdalrahman Hajjo</span>, DashWeb Studio began as a one-person operation serving local businesses in Tripoli. Today, we've grown into a respected development studio working with clients across Lebanon and internationally.
              </motion.p>
              <motion.p variants={itemVariants}>
                Our mission is to bridge the gap between innovative ideas and technical execution, helping businesses transform their vision into reality through cutting-edge technology and thoughtful design.
              </motion.p>
              <motion.p variants={itemVariants}>
                We combine technical expertise with creative problem-solving to deliver high-quality web applications that are efficient, maintainable, and provide exceptional user experiences.
              </motion.p>
            </motion.div>
          </motion.div>

          {/* Stats section */}
          
        </div>
      </section>

      {/* Technologies Section */}
      <section className="tech-section">
        {/* Floating particles */}
        <AnimatePresence>
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="floating-particle"
              initial={{
                x: Math.random() * window.innerWidth,
                y: Math.random() * 1000,
                opacity: 0
              }}
              animate={{
                y: -1000,
                opacity: [0, 0.8, 0],
                transition: {
                  duration: 10 + Math.random() * 20,
                  repeat: Infinity,
                  delay: Math.random() * 5
                }
              }}
              exit={{ opacity: 0 }}
            />
          ))}
        </AnimatePresence>
        
        <div className="section-container">
          <motion.div
            className="section-header"
            initial="hidden"
            animate={techControls}
            variants={containerVariants}
          >
            <motion.h2
              className="section-title"
              variants={itemVariants}
            >
              Technologies We <span className="text-accent">Use</span>
            </motion.h2>
            <motion.p
              className="section-subtitle"
              variants={itemVariants}
            >
              Modern tools and frameworks to build performant, scalable applications
            </motion.p>
          </motion.div>

          <div className="tech-scroll-container">
            <div
              ref={techScrollRef}
              className="tech-scroll-content"
            >
              {doubledTechnologies.map((tech, index) => (
                <motion.div
                  key={index}
                  className="tech-card"
                  custom={index % technologies.length}
                  initial="hidden"
                  animate="visible"
                  variants={cardVariants}
                  whileHover={{ 
                    y: -15,
                    boxShadow: "0 15px 30px rgba(237, 121, 49, 0.2)",
                    borderColor: "rgba(237, 121, 49, 0.3)"
                  }}
                >
                  <motion.div 
                    className="tech-icon"
                    style={{ color: tech.color }}
                    whileHover={{ 
                      scale: 1.1,
                      rotate: [0, 10, -10, 0],
                      transition: { duration: 0.6 }
                    }}
                  >
                    {tech.icon}
                  </motion.div>
                  <h3>{tech.name}</h3>
                  <p>{tech.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="clients-section">
        {/* Animated gradient background */}
        <motion.div 
          className="gradient-bg"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%']
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        
        <div className="section-container">
          <motion.div
            className="section-header"
            initial="hidden"
            animate={clientControls}
            variants={containerVariants}
          >
            <motion.h2
              className="section-title"
              variants={itemVariants}
            >
              Trusted <span className="text-accent">By</span>
            </motion.h2>
            <motion.p
              className="section-subtitle"
              variants={itemVariants}
            >
              Organizations we've had the privilege to work with
            </motion.p>
          </motion.div>

          <div className="clients-scroll-container">
            <div
              ref={clientScrollRef}
              className="clients-scroll-content"
            >
              {doubledClients.map((client, index) => (
                <motion.div
                  key={index}
                  className="client-card"
                  custom={index % clients.length}
                  initial="hidden"
                  animate="visible"
                  variants={cardVariants}
                  whileHover={{ 
                    scale: 1.05,
                    backgroundColor: "rgba(237, 121, 49, 0.1)",
                    borderColor: "rgba(237, 121, 49, 0.5)"
                  }}
                >
                  <motion.div 
                    className="client-logo"
                    whileHover={{ 
                      rotate: 360,
                      scale: 1.1,
                      transition: { duration: 0.8 }
                    }}
                  >
                    <span>{client.logo}</span>
                  </motion.div>
                  <h3>{client.name}</h3>
                  <p>{client.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        {/* Animated rings */}
        <motion.div 
          className="ring ring-1"
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.3, 0.1, 0.3]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="ring ring-2"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.05, 0.2]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        <motion.div
          initial="hidden"
          animate={controls}
          variants={containerVariants}
          className="cta-content"
        >
          <motion.h2
            className="cta-title"
            variants={itemVariants}
          >
            Ready to start your <span className="text-accent">project</span>?
          </motion.h2>
          <motion.p
            className="cta-text"
            variants={itemVariants}
          >
            Get in touch to discuss how we can help bring your ideas to life with our technical expertise and development experience.
          </motion.p>
          <motion.div variants={itemVariants}>
      <motion.button 
        className="cta-button"
        whileHover={{ 
          scale: 1.05,
          boxShadow: "0 6px 25px rgba(237, 121, 49, 0.5)"
        }}
        whileTap={{ scale: 0.98 }}
        onClick={() => navigate("/contact")}
      >
        <span>Contact Us</span>
        <motion.span
          className="button-hover-effect"
          initial={{ x: '-100%' }}
          whileHover={{ x: '0%' }}
          transition={{ duration: 0.6 }}
        />
      </motion.button>
    </motion.div>
        </motion.div>
      </section>
    </motion.main>
  );
};

export default About;

// CSS Styles (should be in a separate CSS file or CSS-in-JS)
const styles = `
  /* Base Styles */
  .about-container {
    font-family: 'Inter', sans-serif;
    background-color: var(--primary);
    color: var(--secondary);
  }

  /* Hero Section */
  .hero-section {
    position: relative;
    padding: 10rem 2rem;
    background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
    overflow: hidden;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .hero-bg-pattern {
    position: absolute;
    inset: 0;
    opacity: 0.05;
    background-image: radial-gradient(circle at center, var(--accent) 1px, transparent 1px);
    background-size: 20px 20px;
  }

  .hero-content {
    max-width: 1200px;
    margin: 0 auto;
    position: relative;
    z-index: 10;
    text-align: center;
  }

  .hero-title {
    font-size: 4rem;
    font-weight: var(--font-extra-bold);
    margin-bottom: 1.5rem;
    color: white;
    letter-spacing: 0.05em;
    line-height: 1.2;
    text-shadow: 0 2px 10px rgba(237, 121, 49, 0.3);
  }

  .hero-subtitle {
    font-size: 1.5rem;
    color: var(--grey-60);
    margin-bottom: 2rem;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
    font-weight: var(--font-light);
  }

  .cta-button {
    position: relative;
    padding: 1rem 2.5rem;
    background-color: var(--accent);
    color: var(--primary);
    font-weight: var(--font-semi-bold);
    border-radius: 9999px;
    border: none;
    cursor: pointer;
    font-size: 1rem;
    box-shadow: 0 4px 15px rgba(237, 121, 49, 0.4);
    transition: all 0.3s ease;
    overflow: hidden;
    z-index: 1;
  }

  .button-hover-effect {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 9999px;
    z-index: -1;
  }

  .scroll-indicator {
    position: absolute;
    bottom: 2rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    color: var(--grey-60);
    font-size: 0.875rem;
  }

  .scroll-line {
    width: 1px;
    height: 50px;
    background: linear-gradient(to bottom, var(--accent), transparent);
    margin-top: 0.5rem;
  }

  /* Story Section */
  .story-section {
    padding: 8rem 2rem;
    background-color: var(--primary);
    position: relative;
    overflow: hidden;
  }

  .grid-bg {
    position: absolute;
    inset: 0;
    background-image: linear-gradient(var(--grey-7) 1px, transparent 1px), 
                      linear-gradient(90deg, var(--grey-7) 1px, transparent 1px);
    background-size: 40px 40px;
    opacity: 0.5;
  }

  .story-content {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 4rem;
    position: relative;
    z-index: 1;
  }

  .story-header {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .section-title {
    font-size: 3rem;
    font-weight: var(--font-bold);
    color: white;
    margin-bottom: 0.5rem;
  }

  .title-line {
    width: 80px;
    height: 4px;
    background-color: var(--accent);
    margin-bottom: 2rem;
    transform-origin: left;
  }

  .section-subtitle {
    color: var(--accent);
    font-weight: var(--font-semi-bold);
    font-size: 1.25rem;
  }

  .story-text {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    max-width: 800px;
  }

  .story-text p {
    font-size: 1.125rem;
    line-height: 1.75;
    color: var(--grey-60);
  }

  .text-accent {
    color: var(--accent);
  }

  /* Stats Grid */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 2rem;
    margin-top: 4rem;
  }

  .stat-card {
    background-color: var(--grey-7);
    padding: 2rem;
    border-radius: 0.75rem;
    border: 1px solid var(--grey-10);
    transition: all 0.3s ease;
    text-align: center;
  }

  .stat-value {
    font-size: 2.5rem;
    font-weight: var(--font-bold);
    color: var(--accent);
    margin-bottom: 0.5rem;
  }

  .stat-label {
    font-size: 1rem;
    color: var(--grey-60);
  }

  /* Technologies Section */
  .tech-section {
    padding: 8rem 2rem;
    background-color: #111;
    position: relative;
    overflow: hidden;
  }

  .floating-particle {
    position: absolute;
    width: 2px;
    height: 2px;
    background-color: rgba(237, 121, 49, 0.5);
    border-radius: 50%;
  }

  .section-container {
    max-width: 1200px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  .section-header {
    text-align: center;
    margin-bottom: 4rem;
  }

  .tech-scroll-container {
    position: relative;
    overflow: hidden;
  }

  .tech-scroll-content {
    display: flex;
    overflow-x: hidden;
    padding: 2rem 0;
    gap: 2rem;
    align-items: center;
  }

  .tech-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 220px;
    padding: 2rem;
    background-color: var(--grey-7);
    border-radius: 0.75rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;
    border: 1px solid var(--grey-10);
  }

  .tech-icon {
    margin-bottom: 1.5rem;
    transition: all 0.3s ease;
  }

  .tech-card h3 {
    font-weight: var(--font-semi-bold);
    color: white;
    margin-bottom: 0.5rem;
    font-size: 1.25rem;
  }

  .tech-card p {
    font-size: 0.875rem;
    color: var(--grey-60);
    text-align: center;
    line-height: 1.5;
  }

  /* Clients Section */
  .clients-section {
    padding: 8rem 2rem;
    background-color: #0d0d0d;
    color: white;
    position: relative;
    overflow: hidden;
  }

  .gradient-bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(45deg, rgba(237, 121, 49, 0.1), rgba(255, 150, 80, 0.1));
    opacity: 0.3;
  }

  .clients-scroll-container {
    position: relative;
    overflow: hidden;
  }

  .clients-scroll-content {
    display: flex;
    overflow-x: hidden;
    padding: 2rem 0;
    gap: 2rem;
    align-items: center;
  }

  .client-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 300px;
    padding: 2rem;
    background-color: var(--grey-7);
    border-radius: 0.75rem;
    border: 1px solid var(--grey-10);
    transition: all 0.3s ease;
  }

  .client-logo {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: rgba(237, 121, 49, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    border: 2px solid var(--accent);
    font-weight: var(--font-bold);
    font-size: 2rem;
    color: var(--accent);
  }

  .client-card h3 {
    font-weight: var(--font-semi-bold);
    font-size: 1.25rem;
    margin-bottom: 0.5rem;
    text-align: center;
  }

  .client-card p {
    font-size: 0.875rem;
    color: var(--grey-60);
    text-align: center;
  }

  /* CTA Section */
  .cta-section {
    padding: 10rem 2rem;
    background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
    color: white;
    text-align: center;
    position: relative;
    overflow: hidden;
  }

  .ring {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(237, 121, 49, 0.2);
    z-index: 0;
  }

  .ring-1 {
    top: 50%;
    left: 20%;
    width: 300px;
    height: 300px;
  }

  .ring-2 {
    top: 30%;
    right: 15%;
    width: 400px;
    height: 400px;
  }

  .cta-content {
    max-width: 800px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  .cta-title {
    font-size: 3rem;
    font-weight: var(--font-bold);
    margin-bottom: 1.5rem;
    line-height: 1.2;
  }

  .cta-text {
    font-size: 1.25rem;
    color: var(--grey-60);
    margin-bottom: 2.5rem;
    line-height: 1.6;
  }

  /* Responsive Styles */
  @media (max-width: 980px) {
    .hero-title {
      font-size: 2.5rem;
    }

    .hero-subtitle {
      font-size: 1.25rem;
    }

    .section-title {
      font-size: 2rem;
    }

    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .tech-card, .client-card {
      min-width: 250px;
    }
  }

  @media (max-width: 640px) {
    .hero-section, .story-section, .tech-section, .clients-section, .cta-section {
      padding: 6rem 1.5rem;
    }

    .hero-title {
      font-size: 2rem;
    }

    .section-title {
      font-size: 1.75rem;
    }

    .stats-grid {
      grid-template-columns: 1fr;
    }

    .tech-card, .client-card {
      min-width: 200px;
    }
  }
`;

// Inject styles
const styleElement = document.createElement('style');
styleElement.innerHTML = styles;
document.head.appendChild(styleElement);
